/** Pure, immutable operations on a tag-category schema tree.
 *
 * The SchemaEditor is a controlled component: every mutation returns a new
 * `TagSchema` via these helpers (never mutates in place), so React state updates
 * and undo/redo stay predictable. Kept UI-free + side-effect-free so they can be
 * unit-tested directly (see `schemaEditorOps.test.ts`).
 */
import {
  X_DISPLAY_ORDER,
  X_GROUP,
  X_GROUPS,
  UNGROUPED,
  groupedProps,
  groupsOf,
  type JsonSchemaType,
  type SchemaGroup,
  type SchemaProp,
  type TagSchema,
} from './vocabulary';

export function emptySchema(): TagSchema {
  return { type: 'object', properties: {}, required: [] };
}

/** A fresh property node for a newly-added key of the given type. */
export function defaultForType(type: JsonSchemaType): SchemaProp {
  if (type === 'object') return { type, properties: {} };
  if (type === 'array') return { type, items: { type: 'string' } };
  return { type };
}

/** Add a top-level property. No-op if the key already exists / is blank. */
export function addProperty(schema: TagSchema, key: string, type: JsonSchemaType): TagSchema {
  const k = key.trim();
  const props = schema.properties ?? {};
  if (!k || k in props) return schema;
  return { ...schema, properties: { ...props, [k]: defaultForType(type) } };
}

/** Replace one property node (by key). */
export function setProperty(schema: TagSchema, key: string, prop: SchemaProp): TagSchema {
  const props = schema.properties ?? {};
  if (!(key in props)) return schema;
  return { ...schema, properties: { ...props, [key]: prop } };
}

/** Remove a property + drop it from `required`. */
export function removeProperty(schema: TagSchema, key: string): TagSchema {
  const props = { ...(schema.properties ?? {}) };
  if (!(key in props)) return schema;
  delete props[key];
  const required = (schema.required ?? []).filter((r) => r !== key);
  return { ...schema, properties: props, required };
}

/** Rename a property, preserving insertion order + `required` membership. */
export function renameProperty(schema: TagSchema, oldKey: string, newKey: string): TagSchema {
  const nk = newKey.trim();
  const props = schema.properties ?? {};
  if (!nk || oldKey === nk || !(oldKey in props) || nk in props) return schema;
  const rebuilt: Record<string, SchemaProp> = {};
  for (const [k, v] of Object.entries(props)) {
    rebuilt[k === oldKey ? nk : k] = v;
  }
  const required = (schema.required ?? []).map((r) => (r === oldKey ? nk : r));
  return { ...schema, properties: rebuilt, required };
}

/** Rename a nested child property (on a `SchemaProp.properties` map), preserving
 *  insertion order. No `required` array at nested level. No-op on blank / clash. */
export function renameChildProperty(prop: SchemaProp, oldKey: string, newKey: string): SchemaProp {
  const nk = newKey.trim();
  const props = prop.properties ?? {};
  if (!nk || oldKey === nk || !(oldKey in props) || nk in props) return prop;
  const rebuilt: Record<string, SchemaProp> = {};
  for (const [k, v] of Object.entries(props)) {
    rebuilt[k === oldKey ? nk : k] = v;
  }
  return { ...prop, properties: rebuilt };
}

/** Toggle a property's presence in the schema's `required` array. */
export function setRequired(schema: TagSchema, key: string, required: boolean): TagSchema {
  const cur = schema.required ?? [];
  const has = cur.includes(key);
  if (required === has) return schema;
  return {
    ...schema,
    required: required ? [...cur, key] : cur.filter((r) => r !== key),
  };
}

// --- Groups -----------------------------------------------------------------

export function addGroup(schema: TagSchema, group: SchemaGroup): TagSchema {
  const name = group.name.trim();
  const groups = schema[X_GROUPS] ?? [];
  if (!name || groups.some((g) => g.name === name)) return schema;
  const order = groups.length;
  return { ...schema, [X_GROUPS]: [...groups, { ...group, name, order }] };
}

/** Patch one group, found by its stable `name`. */
export function updateGroup(schema: TagSchema, name: string, patch: Partial<SchemaGroup>): TagSchema {
  const groups = schema[X_GROUPS] ?? [];
  const index = groups.findIndex((g) => g?.name === name);
  const target = groups[index];
  if (!target) return schema;
  const next = groups.slice();
  next[index] = { ...target, ...patch };
  return { ...schema, [X_GROUPS]: next };
}

/** Remove a group + clear every property's `x-group` reference to it. */
export function removeGroup(schema: TagSchema, name: string): TagSchema {
  const groups = (schema[X_GROUPS] ?? []).filter((g) => g.name !== name);
  const props = schema.properties ?? {};
  const nextProps: Record<string, SchemaProp> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v?.[X_GROUP] === name) {
      const rest = { ...v };
      delete rest[X_GROUP];
      nextProps[k] = rest;
    } else {
      nextProps[k] = v;
    }
  }
  return { ...schema, [X_GROUPS]: groups, properties: nextProps };
}

/** Move a group up (dir=-1) or down (dir=+1) in display order (`order`, as
 *  `groupsOf` lists them), renumbering `order` 0..N-1. Found by `name`. */
export function moveGroup(schema: TagSchema, name: string, dir: -1 | 1): TagSchema {
  const groups = groupsOf(schema);
  const index = groups.findIndex((g) => g.name === name);
  const j = index + dir;
  const a = groups[index];
  const b = groups[j];
  if (!a || !b) return schema;
  groups[index] = b;
  groups[j] = a;
  return { ...schema, [X_GROUPS]: groups.map((g, i) => ({ ...g, order: i })) };
}

// --- Property placement (the two-column editor, CP-7988) -----------------------

/** One column of the editor: a declared group (empty ones too) or Ungrouped. */
export interface PropertyList {
  id: string; // the group's name, or UNGROUPED
  group: SchemaGroup | null;
  keys: string[];
}

/** Every declared group in display order with its property keys (by
 *  `x-displayOrder`), then Ungrouped — which also holds properties naming an
 *  undeclared group. Unlike `groupedProps`, empty groups are kept: they are
 *  drop targets. */
export function propertyLists(schema: TagSchema): PropertyList[] {
  const byGroup = new Map(groupedProps(schema).map((s) => [s.group?.name ?? UNGROUPED, s.entries.map(([k]) => k)]));
  return [
    ...groupsOf(schema).map((g) => ({ id: g.name, group: g, keys: byGroup.get(g.name) ?? [] })),
    { id: UNGROUPED, group: null, keys: byGroup.get(UNGROUPED) ?? [] },
  ];
}

/** Move `keys` (in the given order) into `target` (a group name, or UNGROUPED)
 *  just before `beforeKey`, or at its end when `beforeKey` is null; a
 *  `beforeKey` that is itself moving keeps its place. Moved properties get
 *  `x-group` set to the target (removed for
 *  Ungrouped); then `x-displayOrder` is renumbered 0..N-1 across every
 *  top-level property in the order the editor lists them, so every other
 *  property keeps its place. Returns the same schema when nothing would change
 *  or the target / keys are unknown. */
export function moveProperties(
  schema: TagSchema,
  keys: string[],
  target: string,
  beforeKey: string | null,
): TagSchema {
  const props = schema.properties ?? {};
  const moving = keys.filter((k, i) => k in props && keys.indexOf(k) === i);
  const lists = propertyLists(schema);
  if (!moving.length || !lists.some((l) => l.id === target)) return schema;

  const before = lists.flatMap((l) => l.keys);
  const next = lists.map((l) => ({ ...l, keys: l.keys.filter((k) => !moving.includes(k)) }));
  let anchor = beforeKey;
  if (anchor && moving.includes(anchor)) {
    const was = lists.find((l) => l.id === target)!.keys;
    const i = was.indexOf(anchor);
    anchor = i < 0 ? null : (was.slice(i + 1).find((k) => !moving.includes(k)) ?? null);
  }
  const into = next.find((l) => l.id === target)!;
  const at = anchor ? into.keys.indexOf(anchor) : -1;
  into.keys.splice(at < 0 ? into.keys.length : at, 0, ...moving);
  const after = next.flatMap((l) => l.keys);

  const groupOf = (k: string) => props[k]?.[X_GROUP];
  const regrouped = moving.some((k) => (target === UNGROUPED ? groupOf(k) !== undefined : groupOf(k) !== target));
  if (!regrouped && before.every((k, i) => k === after[i])) return schema;

  const out: Record<string, SchemaProp> = {};
  after.forEach((k, i) => {
    const prop: SchemaProp = { ...props[k], [X_DISPLAY_ORDER]: i };
    if (moving.includes(k)) {
      if (target === UNGROUPED) delete prop[X_GROUP];
      else prop[X_GROUP] = target;
    }
    out[k] = prop;
  });
  return { ...schema, properties: out };
}

/** Where a drop lands: over a column's own drop zone (`container`) → its end;
 *  over a property → before it, or before the next property that is not itself
 *  moving when `after`. Null when it would land on a moving property. */
export function resolveDrop(
  lists: PropertyList[],
  moving: string[],
  over: { container: string } | { key: string },
  after: boolean,
): { target: string; beforeKey: string | null } | null {
  if ('container' in over) {
    return lists.some((l) => l.id === over.container) ? { target: over.container, beforeKey: null } : null;
  }
  if (moving.includes(over.key)) return null;
  const list = lists.find((l) => l.keys.includes(over.key));
  if (!list) return null;
  if (!after) return { target: list.id, beforeKey: over.key };
  const rest = list.keys.slice(list.keys.indexOf(over.key) + 1).filter((k) => !moving.includes(k));
  return { target: list.id, beforeKey: rest[0] ?? null };
}

// --- Property field / extension setters (used by the property editor) --------

/** Set (or clear, when value is undefined/'') a scalar property field. */
export function setPropField<K extends keyof SchemaProp>(
  prop: SchemaProp,
  field: K,
  value: SchemaProp[K] | undefined,
): SchemaProp {
  const next = { ...prop };
  if (value === undefined || value === '') {
    delete next[field];
  } else {
    next[field] = value;
  }
  return next;
}

/** Set a boolean `x-` flag, deleting the key when false (keeps schemas tidy). */
export function setPropFlag(prop: SchemaProp, flag: string, on: boolean): SchemaProp {
  const next = { ...prop };
  if (on) next[flag] = true;
  else delete next[flag];
  return next;
}

/** Change a property's type, resetting type-specific sub-structure. */
export function setPropType(prop: SchemaProp, type: JsonSchemaType): SchemaProp {
  const next: SchemaProp = { ...prop, type };
  if (type === 'object') {
    if (!next.properties) next.properties = {};
    delete next.items;
  } else if (type === 'array') {
    if (!next.items) next.items = { type: 'string' };
    delete next.properties;
  } else {
    delete next.properties;
    delete next.items;
  }
  return next;
}

/** Normalize the display order of a property list to 0..N-1 by current order. */
export function reindexDisplayOrder(entries: [string, SchemaProp][]): Record<string, SchemaProp> {
  const out: Record<string, SchemaProp> = {};
  entries.forEach(([k, v], i) => {
    out[k] = { ...v, [X_DISPLAY_ORDER]: i };
  });
  return out;
}
